import type { AnyAction, Middleware } from 'redux';
import axios from 'axios';
import { createEventForwardingConnection } from '../../../services/WsConnection';
import { eventForwardingConnectionClosed, types as mainTypes } from '../../actions/backendMain';
import { reload, types as commonTypes } from '../../actions/backendCommon';
import { errorCodes } from '../../../utils/utils';
import { proto } from '../../../proto/ListenerProto';
import { forwardingLogger } from '../../../utils/logger/loggerFactory';
import { captureException, captureInfoMessage, captureWarnMessage } from '../../../tracker/raven';
import { parse } from '../../../services/EventsValidator';
import { getCallId } from '../../selectors/call';
import { enableCallEventsPoller, getCallDetectionAppWhitelistConfig, getExplicitIntegrationSource } from '../../selectors/config';
import { getAuthToken } from '../../selectors/auth';
import type { RootState } from '../../types';
import { types as callTypes } from '../../actions/backendCall';
import { CALL_EVENT_URL } from '../../../utils/config';
import { CallEvents } from '../../../constants/backend';
import { dispatchBackend } from '../../types/backend';
import type { BackendAppConfigCallDetection } from '../../types';

const { EVENT_FORWARDING_CONNECTION_CLOSED } = errorCodes.background;

const START_EVENT_PROCESSING_DELAY = 1500;
const fwdLog = forwardingLogger;

interface QueueItem {
    callEvent: string;
    eventBody: Record<string, unknown>;
    callMode?: number;
    forgetAudioAndTranscript?: boolean;
    callID?: string;
    addedAt: number;
    /**
     * Event timestamp (ms since epoch) parsed from `eventBody.timestamp`.
     * Used to re-order the queue when the transport (Electron IPC) delivers
     * two rapid events out of order. Falls back to `addedAt` when the event
     * has no parseable `timestamp` field (old telephony integrations).
     */
    eventTs: number;
}

/**
 * Extract a monotonic "event time" from an incoming call event body.
 *
 * Telephony events carry an ISO timestamp in `eventBody.timestamp`. When two
 * events arrive over Electron IPC in quick succession (e.g. `call_end(old)` at
 * T and `call_start(new)` at T+112ms) the IPC layer does NOT guarantee
 * delivery order, so we rely on the producer-side timestamp to order them.
 *
 * Returns `fallback` if the timestamp is missing or unparseable — we never
 * want to drop an event because of a bad timestamp.
 */
function parseEventTimestamp(eventBody: Record<string, unknown>, fallback: number): number {
    const ts = eventBody.timestamp;
    if (typeof ts === 'string' || typeof ts === 'number') {
        const parsed = new Date(ts).getTime();
        if (Number.isFinite(parsed)) return parsed;
    }
    return fallback;
}

export class EventQueue {
    private dispatch: (action: unknown) => void;

    private queue: QueueItem[];

    private _working: boolean;

    constructor(dispatch: (action: unknown) => void) {
        this.dispatch = dispatch;
        this.queue = [];
        this._working = false;
    }

    add({
        callEvent,
        eventBody,
        callMode,
        forgetAudioAndTranscript
    }: {
        callEvent: string;
        eventBody: Record<string, unknown>;
        callMode?: number;
        forgetAudioAndTranscript?: boolean;
    }) {
        const callID = getCallIdFromEventBody(eventBody);
        if (callID) {
            // If a 'stop' event is received for a call ID while a 'start' event is still queued,
            // remove all events with that call ID from the queue.
            if (
                callEvent === CallEvents.END &&
                this.queue.findIndex((item: QueueItem) => item.callID === callID && item.callEvent === CallEvents.START) > -1
            ) {
                captureInfoMessage('event queue coalesced start+end', {
                    type: 'eventQueueCoalesce',
                    callID,
                    queueSizeBefore: this.queue.length
                });
                this.queue = this.queue.filter((item: QueueItem) => item.callID !== callID);
                return;
            }
        }

        const addedAt = Date.now();
        const item: QueueItem = {
            callEvent,
            eventBody,
            callMode,
            forgetAudioAndTranscript,
            callID,
            addedAt,
            eventTs: parseEventTimestamp(eventBody, addedAt)
        };

        // Insert while preserving ascending order by `eventTs`. This protects
        // against IPC reordering of call_end(old) and call_start(new) of
        // different callIDs that arrive ~100 ms apart from the embedded HTTP
        // integration. We only reorder events that have NOT been dispatched
        // yet (still in the queue); anything already dispatched is out of
        // our hands.
        //
        // Stable w.r.t. existing items with equal timestamp: new item is
        // placed AFTER existing items with the same `eventTs`, preserving
        // insertion order for same-tick events.
        let insertAt = this.queue.length;
        for (let i = 0; i < this.queue.length; i += 1) {
            if (this.queue[i].eventTs > item.eventTs) {
                insertAt = i;
                break;
            }
        }
        if (insertAt === this.queue.length) {
            this.queue.push(item);
        } else {
            // Out-of-order arrival: a newer-by-arrival event has an older `eventTs`
            // than something already in the queue. Logged only when reordering
            // actually happens (not on every push) to avoid noise.
            captureInfoMessage('event queue reordered insert', {
                type: 'eventQueueReorder',
                callID,
                callEvent,
                insertAt,
                queueSize: this.queue.length,
                incomingEventTs: item.eventTs,
                blockingEventTs: this.queue[insertAt].eventTs,
                blockingCallEvent: this.queue[insertAt].callEvent
            });
            this.queue.splice(insertAt, 0, item);
        }

        this.run().catch((e: Error) => captureException(e));
    }

    async run() {
        if (this._working) return;

        this._working = true;

        const getNext = async () => {
            // wait for some delay before process START events
            if (
                this.queue.length > 0 &&
                this.queue[0].callEvent === CallEvents.START &&
                Date.now() - this.queue[0].addedAt < START_EVENT_PROCESSING_DELAY
            ) {
                await new Promise<void>(resolve =>
                    setTimeout(resolve, START_EVENT_PROCESSING_DELAY - (Date.now() - this.queue[0].addedAt))
                );
            }

            return this.queue.shift();
        };

        let nextEvent = await getNext();
        while (nextEvent) {
            this.dispatch({
                type: callTypes.CALL_EVENT_RECEIVED,
                payload: {
                    callEvent: nextEvent.callEvent,
                    eventBody: nextEvent.eventBody,
                    callMode: nextEvent.callMode,
                    forgetAudioAndTranscript: nextEvent.forgetAudioAndTranscript
                }
            });

            nextEvent = await getNext();
        }

        this._working = false;
    }
}

interface ForwardingConnection {
    isOpen: () => boolean;
    close: (code?: unknown) => void;
}
type ParsedEvent = [string, number | undefined, boolean];

function isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function getCallIdFromEventBody(eventBody: Record<string, unknown>): string | undefined {
    return typeof eventBody.call_id === 'string' ? eventBody.call_id : undefined;
}

interface ParseOptions {
    appWhitelist?: string[];
}

function toError(value: unknown): Error {
    return value instanceof Error ? value : new Error(String(value));
}

function isCancelledError(error: unknown): boolean {
    if (typeof error !== 'object' || error === null) {
        return false;
    }
    return (error as Record<string, unknown>).code === 'ERR_CANCELED';
}

const forwardingConnection: Middleware = store => {
    const dispatch = (action: unknown) => dispatchBackend(store.dispatch, action);
    const getState = store.getState as () => RootState;

    const eventQueue = new EventQueue(dispatch);

    function onCallEvent(rawStringContent: string, source: 'ws-local' | 'electron-ipc' | 'http-poll') {
        // Log every event arriving from the internal local listener (WS or Electron IPC)
        // BEFORE any parsing, schema validation, or queue handling. This lets us see whether
        // a missing END was actually delivered to the app or never arrived at all. External
        // HTTP polling is intentionally excluded.
        if (source !== 'http-poll') {
            captureInfoMessage('internal call event raw', {
                type: 'callEventRaw',
                source,
                payload: rawStringContent
            });
        }

        try {
            // @docs https://i2x-gmbh.atlassian.net/wiki/spaces/TECH/pages/2463891471/Telephony+Integration+Space

            const parsedBody = JSON.parse(rawStringContent);
            if (!isRecord(parsedBody)) {
                captureWarnMessage('call state event parse error', {
                    type: 'callEvent',
                    error: 'event body is not an object',
                    payload: rawStringContent
                });
                return;
            }
            const eventBody = parsedBody;
            const currentState = getState();
            const appWhitelist = getCallDetectionAppWhitelistConfig(currentState);
            const explicitIntegrationSource = getExplicitIntegrationSource(currentState);
            const callID = getCallId(currentState);
            const parseOptions: ParseOptions = { appWhitelist };

            if (explicitIntegrationSource == null) {
                captureWarnMessage('call state event parse error', {
                    type: 'callEvent',
                    error: 'explicit integration source is not configured',
                    payload: rawStringContent,
                    callID
                });
                return;
            }

            const [callEvent, callMode, forgetAudioAndTranscript] = parse(
                explicitIntegrationSource as NonNullable<BackendAppConfigCallDetection['explicitIntegrationSource']>,
                eventBody,
                parseOptions
            ) as ParsedEvent;

            if (callEvent) {
                eventQueue.add({
                    callEvent,
                    eventBody,
                    callMode,
                    forgetAudioAndTranscript
                });
                // dispatch({
                //     type: callTypes.CALL_EVENT_RECEIVED,
                //     payload: {
                //         callEvent,
                //         eventBody,
                //         callMode,
                //         forgetAudioAndTranscript
                //     }
                // });
            } else {
                captureWarnMessage('call state event parse error', {
                    type: 'callEvent',
                    error: 'unhandled call event',
                    payload: rawStringContent,
                    callID
                });
            }
        } catch (e) {
            captureException(toError(e));
        }
    }

    if (window.electronAPI?.onCallEvent) {
        window.electronAPI.onCallEvent(([callEvent]: [unknown]) => {
            onCallEvent(JSON.stringify(callEvent), 'electron-ipc');
        });
    }

    function createForwardingConnection({ port }: { port?: number }) {
        fwdLog.log('createForwardingConnection on port', port);
        const ws = createEventForwardingConnection({ host: 'localhost', port });
        ws.on('opened', () => {
            fwdLog.log('ForwardingConnection opened on port', port);
        });
        ws.on('closed', ({ wasClean }: { wasClean: boolean }) => {
            fwdLog.log('ForwardingConnection closed. WasClean:', wasClean);
            dispatch(eventForwardingConnectionClosed(wasClean));
        });
        ws.on('message', (data: Uint8Array) => {
            const { rawStringContent } = proto.BroadcastedEvent.decode(data);
            if (rawStringContent) {
                onCallEvent(rawStringContent, 'ws-local');
            }
        });

        return ws;
    }

    let onCloseEventGetter: null | (() => void) = null;
    const eventGetter = () => {
        let controller: AbortController | null = null;

        const fetchAndDispatch = async () => {
            controller = new AbortController();

            const jwtToken = getAuthToken(getState());
            if (!jwtToken) {
                fwdLog.error('[FetchCallEvents] JWT is not set');
                return;
            }

            const response = await axios.get(CALL_EVENT_URL, {
                headers: { Authorization: `Bearer ${jwtToken}` },
                signal: controller.signal
            });

            const event = isRecord(response.data) ? response.data : null;
            if (event) {
                const origTime = new Date(String(event.timestamp)).getTime();
                const timeDiff = Math.abs(Date.now() - origTime);

                if (timeDiff > 120000) {
                    fwdLog.warn('FetchCallEvents: bad event timing');
                }

                event.source = event.source || 'call-events-api';
                event.version = event.version || '1.0';

                onCallEvent(JSON.stringify(event), 'http-poll');
            }
        };

        let doNextFetch = true;
        (async () => {
            while (doNextFetch) {
                try {
                    await fetchAndDispatch();
                } catch (e: unknown) {
                    if (!isCancelledError(e)) {
                        captureException(toError(e));

                        // wait 1 sec before the next fetch in case of error
                        await new Promise(resolve => setTimeout(resolve, 1000));
                    }
                }
            }
        })();

        captureInfoMessage('Event Getter Started');

        return () => {
            doNextFetch = false;
            if (controller) {
                controller.abort();
            }

            captureInfoMessage('Event Getter Stoped');
        };
    };

    let connection: ForwardingConnection | null = null;

    return next => action => {
        const typedAction = action as AnyAction;
        const { type, payload } = typedAction || {};
        const result = next(action);

        switch (type) {
            case mainTypes.START_EVENT_FORWARDING_CONNECTION: {
                connection = createForwardingConnection(payload);
                break;
            }
            case callTypes.CALL_DETECTION_STARTED: {
                const currentState = getState();
                if (!onCloseEventGetter && enableCallEventsPoller(currentState)) {
                    onCloseEventGetter = eventGetter();
                }
                break;
            }
            case commonTypes.RELOAD: {
                if (onCloseEventGetter) {
                    onCloseEventGetter();
                    onCloseEventGetter = null;
                }
                break;
            }
            case commonTypes.CLOSE_ALL_CONNECTIONS: {
                const { code } = payload || {};
                if (connection && connection.isOpen()) {
                    connection.close(code);
                }
                break;
            }
            case mainTypes.EVENT_FORWARDING_CONNECTION_CLOSED: {
                const { wasClean } = payload || {};

                // TOFIX: listener ws connection returns default code
                // Need to get state because WSConnection always return code 1000
                const {
                    backend: {
                        main: { isTrainerActive }
                    }
                } = getState();

                if (isTrainerActive && !wasClean) {
                    dispatch(
                        reload({
                            code: EVENT_FORWARDING_CONNECTION_CLOSED,
                            reason: 'EVENT_FORWARDING_CONNECTION_CLOSED'
                        })
                    );
                }
                break;
            }
            default:
        }

        return result;
    };
};

export default forwardingConnection;
